import React, { useState } from "react";
import Tour from "src/components/Tour";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";

import { userDontShowDialogsSelector } from "src/store/system/selector";
import DontShowThisMessageAgainCheckbox from "src/components/DontShowThisMessageAgainCheckbox";

const Key = "walk_through_categorize_expenses";

function scrollIntoViewAndThen(selector: string, callback: () => void) {
  const element = document.querySelector(selector);
  if (!element) {
    callback();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        observer.disconnect();
        callback();
      }
    },
    { threshold: 0.5 }
  );

  observer.observe(element);
  element.scrollIntoView({ behavior: "auto", block: "center" });
}

const getSteps = (onExpandFirstCard?: (onReady: () => void) => void) => [
  {
    selector: '[data-tour="category-card"]',
    content: (props) => (
      <>
        <DialogTitle>Approve Transactions - 1/3</DialogTitle>
        <DialogContent>
          <DialogContentText>
            When new expenses are imported into MRG, you'll be prompted to
            review them. Transactions are grouped by their category for faster
            review.
          </DialogContentText>
          <DialogContentText>
            Click the dropdown to see individual transactions. Or click
            "Approval All" to approve all transactions in the category.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onExpandFirstCard?.(() => {
                props.goTo(1);
              });
            }}
          >
            Next
          </Button>
        </DialogActions>
      </>
    ),
  },
  {
    selector: '[data-tour="transaction-checkboxes"]',
    content: (props) => (
      <>
        <DialogTitle>Select Transactions - 2/3</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You can select or deselect transactions by clicking them or the
            check box in the header of the table.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => props.goTo(0)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              scrollIntoViewAndThen('[data-tour="action-buttons"]', () => {
                props.goTo(2);
              });
            }}
          >
            Next
          </Button>
        </DialogActions>
      </>
    ),
  },
  {
    selector: '[data-tour="action-buttons"]',
    content: (props) => (
      <>
        <DialogTitle>Bulk Actions - 3/3</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You can bulk approve, recategorize, or delete transactions by
            clicking the buttons below the table.
          </DialogContentText>
          <DontShowThisMessageAgainCheckbox dialog={Key} />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              scrollIntoViewAndThen(
                '[data-tour="transaction-checkboxes"]',
                () => {
                  props.goTo(1);
                }
              );
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => props.close()}
          >
            Close
          </Button>
        </DialogActions>
      </>
    ),
  },
];

const useStyles = makeStyles((theme) => ({
  helper: {
    maxWidth: "80%",
    [theme.breakpoints.up("sm")]: {
      maxWidth: "500px",
    },
    padding: 0,
  },
}));

interface WalkThroughProps {
  ready: boolean;
  onExpandFirstCard?: (onReady: () => void) => void;
}

export function WalkThrough({ ready, onExpandFirstCard }: WalkThroughProps) {
  const [open, setOpen] = useState(true);
  const userDontShowDialogs = useSelector(userDontShowDialogsSelector);

  const classes = useStyles();
  const isOpen =
    open && ready && userDontShowDialogs && !userDontShowDialogs?.includes(Key);

  const steps = getSteps(onExpandFirstCard);

  return (
    <Tour
      startAt={0}
      closeWithMask={false}
      disableFocusLock
      steps={steps}
      isOpen={isOpen}
      onRequestClose={() => {
        setOpen(false);
      }}
      showButtons={false}
      showCloseButton={false}
      showNavigation={false}
      showNavigationNumber={false}
      showNumber={false}
      disableDotsNavigation
      rounded={12}
      className={classes.helper}
      disableInteraction
    />
  );
}

export default WalkThrough;
